<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight, Mail, Repeat } from 'lucide-vue-next';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { register } from '@/routes';

const year = new Date().getFullYear();

const product = [
    { label: 'Features', href: '/#modules' },
    { label: 'How it works', href: '/#how' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'FAQ', href: '/#faq' },
];

const platforms = ['LinkedIn', 'X', 'Instagram', 'Facebook', 'TikTok', 'YouTube', 'Threads'];

const company = [
    { label: 'Contact', href: '/contact' },
    { label: 'Privacy policy', href: '/privacy' },
    { label: 'Terms of service', href: '/terms' },
];
</script>

<template>
    <footer class="relative overflow-hidden border-t border-violet-200/60 dark:border-violet-500/20">
        <!-- Soft colorful wash — comfortable, not gray -->
        <div class="absolute inset-0 bg-gradient-to-br from-violet-50 via-fuchsia-50/70 to-cyan-50 dark:from-violet-950/50 dark:via-zinc-950 dark:to-cyan-950/40" />
        <div class="pointer-events-none absolute -top-24 left-1/4 size-72 rounded-full bg-violet-400/15 blur-3xl dark:bg-violet-500/10" aria-hidden="true" />
        <div class="pointer-events-none absolute -right-16 -bottom-28 size-80 rounded-full bg-cyan-400/15 blur-3xl dark:bg-cyan-500/10" aria-hidden="true" />
        <div class="pointer-events-none absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-fuchsia-400/30 to-transparent" aria-hidden="true" />

        <div class="relative mx-auto max-w-7xl px-4 pt-10 pb-6 sm:px-6 sm:pt-12">
            <div class="grid gap-8 lg:grid-cols-12">
                <div class="lg:col-span-5">
                    <Link href="/" class="inline-flex items-center gap-2.5">
                        <AppLogoIcon uid="marketing-footer" class="size-8" />
                        <span class="font-display text-lg font-bold tracking-tight">CreativeTint</span>
                    </Link>
                    <p class="mt-4 max-w-sm text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                        The content loop that learns what works. Create once, publish
                        everywhere, and let every reaction write the brief for your
                        next post.
                    </p>
                    <div class="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-violet-300/50 bg-white/70 px-3 py-1.5 text-xs font-medium text-violet-700 shadow-sm dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-200">
                        <Repeat class="size-3.5 flex-none text-violet-500" />
                        <span class="min-w-0">Create · Publish · Learn · Repeat</span>
                    </div>
                    <div class="mt-5">
                        <Link
                            :href="register()"
                            class="group inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 sm:w-auto"
                        >
                            Start 7-day free trial
                            <ArrowRight class="size-4 transition group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:col-span-7">
                    <div>
                        <h3 class="text-xs font-bold tracking-[0.15em] text-violet-700 uppercase dark:text-violet-300">Features</h3>
                        <ul class="mt-3 space-y-2.5 text-sm">
                            <li v-for="l in product" :key="l.label">
                                <a :href="l.href" class="text-zinc-600 transition hover:text-violet-600 dark:text-zinc-300 dark:hover:text-violet-300">
                                    {{ l.label }}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h3 class="text-xs font-bold tracking-[0.15em] text-cyan-700 uppercase dark:text-cyan-300">Platforms</h3>
                        <ul class="mt-3 space-y-2.5 text-sm text-zinc-600 dark:text-zinc-300">
                            <li v-for="p in platforms" :key="p">{{ p }}</li>
                        </ul>
                    </div>
                    <div class="col-span-2 sm:col-span-1">
                        <h3 class="text-xs font-bold tracking-[0.15em] text-fuchsia-700 uppercase dark:text-fuchsia-300">Company</h3>
                        <ul class="mt-3 space-y-2.5 text-sm">
                            <li v-for="l in company" :key="l.label">
                                <Link :href="l.href" class="text-zinc-600 transition hover:text-fuchsia-600 dark:text-zinc-300 dark:hover:text-fuchsia-300">
                                    {{ l.label }}
                                </Link>
                            </li>
                        </ul>
                        <a
                            href="mailto:support@creativetint.com"
                            class="mt-4 inline-flex max-w-full items-center gap-1.5 break-all text-sm text-zinc-600 transition hover:text-violet-600 dark:text-zinc-300 dark:hover:text-violet-300"
                        >
                            <Mail class="size-3.5 flex-none text-violet-500" /> support@creativetint.com
                        </a>
                    </div>
                </div>
            </div>

            <div class="mt-10 flex flex-col items-center justify-between gap-3 border-t border-violet-200/50 pt-6 text-center text-xs text-zinc-500 md:flex-row md:text-left dark:border-violet-500/20 dark:text-zinc-400">
                <div>© {{ year }} CreativeTint. All rights reserved.</div>
                <div class="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-cyan-600 bg-clip-text font-medium text-transparent">
                    Built for marketing teams that are done guessing.
                </div>
            </div>
        </div>
    </footer>
</template>
